import { useMemoizedFn } from "ahooks";
import { getRestrictionErrors } from "common";
import { isEmpty } from "lodash";
import { useState } from "react";
import { AddOn } from "types";

interface AddOnRestrictionErrors {
    /** Limit error messages, keyed by catalog item id. */
    restrictionErrors: Record<ID, string>;
    /** Override flags sent on each add-on's line item. */
    overrideRestrictions: Record<ID, boolean>;
    /** Returns true when the failure was an exceeded limit, so the caller can skip its own error handling. */
    handleError: (error: unknown) => boolean;
    handleOverrideChange: (value: boolean, templateId?: ID) => void;
    clear: () => void;
    /** Also clears the overrides, for when the selected item changes. */
    reset: () => void;
}

/** Limit errors and overrides per add-on, for screens that show each error on its add-on's row. */
export const useAddOnRestrictionErrors = (addOns: AddOn[]): AddOnRestrictionErrors => {
    const [restrictionErrors, setRestrictionErrors] = useState<Record<ID, string>>({});
    const [overrideRestrictions, setOverrideRestrictions] = useState<Record<ID, boolean>>({});

    const handleError = useMemoizedFn((error: unknown) => {
        const rowErrors = getRestrictionErrors(error, addOns);
        // Keep existing row errors when a later failure is not a limit error.
        if (isEmpty(rowErrors)) {
            return false;
        }

        setRestrictionErrors(rowErrors);

        return true;
    });

    const handleOverrideChange = useMemoizedFn((value: boolean, templateId?: ID) => {
        if (!templateId) {
            return;
        }

        setOverrideRestrictions((current) => {
            return current[templateId] === value ? current : { ...current, [templateId]: value };
        });
    });

    const clear = () => setRestrictionErrors((current) => (isEmpty(current) ? current : {}));

    const reset = () => {
        clear();
        setOverrideRestrictions((current) => (isEmpty(current) ? current : {}));
    };

    return { restrictionErrors, overrideRestrictions, handleError, handleOverrideChange, clear, reset };
};
